import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Alert,
    Box,
    CircularProgress,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { importLeaveBalances, LeaveBalanceCsvRow } from '../dataProvider';

interface ImportBalancesDialogProps {
    open: boolean;
    onClose: () => void;
}

const REQUIRED_COLUMNS = ['email', 'leave_type', 'balance'];

const SAMPLE_CSV = `email,leave_type,balance,allocated
abc.xyz@devsoft.vn,Annual,7.5,12`;

function parseCsv (text: string): LeaveBalanceCsvRow[] | null {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 1) return null;

    const header = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
    for (const col of REQUIRED_COLUMNS) {
        if (!header.includes(col)) return null;
    }

    const rows: LeaveBalanceCsvRow[] = [];
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        // Simple CSV splitting (handles quoted fields)
        const values: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let j = 0; j < line.length; j++) {
            const ch = line[j];
            if (ch === '"') {
                if (inQuotes && line[j + 1] === '"') {
                    current += '"';
                    j++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === ',' && !inQuotes) {
                values.push(current);
                current = '';
            } else {
                current += ch;
            }
        }
        values.push(current);

        const row: Record<string, string> = {};
        header.forEach((h, idx) => {
            row[h] = (values[idx] ?? '').trim();
        });
        rows.push({
            full_name: row['full_name'] ?? '',
            email: row['email'] ?? '',
            leave_type: row['leave_type'] ?? '',
            balance: row['balance'] ?? '',
            leave_units_type: row['leave_units_type'],
            allocated: row['allocated'] !== undefined ? row['allocated'] : undefined,
        });
    }
    return rows;
}

export const ImportBalancesDialog: React.FC<ImportBalancesDialogProps> = ({ open, onClose }) => {
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [mode, setMode] = useState<'override' | 'add'>('override');
    const [rows, setRows] = useState<LeaveBalanceCsvRow[] | null>(null);
    const [parseError, setParseError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleDownloadSample = () => {
        const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'leave_balance_sample.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            const parsed = parseCsv(text);
            if (!parsed) {
                setParseError(
                    `Invalid CSV. Required columns: ${REQUIRED_COLUMNS.join(', ')}`
                );
                setRows(null);
            } else {
                setParseError(null);
                setRows(parsed);
            }
        };
        reader.readAsText(file);
    };

    const handleClose = () => {
        setRows(null);
        setParseError(null);
        setMode('override');
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const handleSubmit = async () => {
        if (!rows || rows.length === 0) return;
        setLoading(true);
        try {
            const result = await importLeaveBalances(rows, mode);
            const msg = `Import complete: ${result.updated} updated, ${result.created} created.`;
            notify(msg, { type: 'success' });
            if (result.warnings && result.warnings.length > 0) {
                result.warnings.forEach((w) => notify(w, { type: 'warning' }));
            }
            handleClose();
        } catch (error: any) {
            const errorMessage =
                error?.body?.message || error?.message || 'Import failed';
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Import Leave Balances</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Upload a CSV file with columns:{' '}
                        <strong>email, leave_type, balance</strong> (plus optional{' '}
                        <em>full_name</em>, <em>leave_units_type</em>, and{' '}
                        <em>allocated</em>). When <em>allocated</em> is provided, it
                        sets <em>allocated_days</em> directly.
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                        />
                        <Button
                            size="small"
                            variant="text"
                            onClick={handleDownloadSample}
                        >
                            Download Sample CSV
                        </Button>
                    </Box>
                </Box>

                {parseError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {parseError}
                    </Alert>
                )}

                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend">Import Mode</FormLabel>
                    <RadioGroup
                        row
                        value={mode}
                        onChange={(e) => setMode(e.target.value as 'override' | 'add')}
                    >
                        <FormControlLabel
                            value="override"
                            control={<Radio />}
                            label="Override (replace existing balance)"
                        />
                        <FormControlLabel
                            value="add"
                            control={<Radio />}
                            label="Add (increment existing balance)"
                        />
                    </RadioGroup>
                </FormControl>

                {rows && rows.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Preview ({rows.length} row{rows.length !== 1 ? 's' : ''})
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Leave Type</TableCell>
                                        <TableCell>Balance</TableCell>
                                        {rows.some((r) => r.allocated !== undefined && r.allocated !== '') && (
                                            <TableCell>Allocated</TableCell>
                                        )}
                                        <TableCell>Full Name</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>{row.leave_type}</TableCell>
                                            <TableCell>{row.balance}</TableCell>
                                            {rows.some((r) => r.allocated !== undefined && r.allocated !== '') && (
                                                <TableCell>{row.allocated ?? ''}</TableCell>
                                            )}
                                            <TableCell>{row.full_name}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!rows || rows.length === 0 || loading}
                    startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                    {loading ? 'Importing…' : 'Import'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
